import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Card, Row, Col } from 'react-bootstrap';
import { createApiUrl } from '../utils/apiConfig';
import { useAuth } from '../context/AuthContext';
import './User.css';

const User = () => {
  const [user, setUser] = useState(null);
  const [_connectionStatus, setConnectionStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  const { user: authUser } = useAuth();
  const { id } = useParams();

  const getBackNavigation = () => {
    const from = new URLSearchParams(location.search).get('from');

    if (from === 'school-admin' || authUser?.isSchAdmin) {
      return {
        path: '/school-admin-dashboard',
        text: 'Back to Sch Admin Dashboard',
      };
    }

    if (from === 'org-admin' || authUser?.isOrgAdmin) {
      return {
        path: '/org-admin-dashboard',
        text: 'Back to Org Admin Dashboard',
      };
    }

    return {
      path: '/admin-dashboard',
      text: 'Back to Admin Dashboard',
    };
  };

  const backNav = getBackNavigation();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setLoading(true);
        const response = await axios.get(createApiUrl(`/api/user/${id}`), {
          withCredentials: true,
        });
        
        if (response.data.subscriptions && response.data.subscriptions.length > 0) {
        }
        
        setUser(response.data);
      } catch (error) {
        console.error('User component: Error fetching user data:', error);
        console.error('User component: Error response:', error.response?.data);
        setConnectionStatus(`Error connecting to server: ${error.response?.data?.message || error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    checkConnection();
  }, [id]);

  const checkConnection = () => {
    if (!navigator.onLine) {
      setConnectionStatus('You are offline');
    } else {
      setConnectionStatus('');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    if (isNaN(date)) {
      return '';
    }
    return date.toISOString().split('T')[0];
  };



  if (loading) {
    return (
      <div className="user-detail-container">
        <div className="container">
          <div className="user-detail-main-content">
            <div className="user-detail-header">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="page-title">User Details</h2>
                <button
                  onClick={() => navigate(backNav.path)}
                  className="btn-back"
                >
                  {backNav.text}
                </button>
              </div>
            </div>
            <div className="text-center">
              <p>Loading...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="user-detail-container">
        <div className="container">
          <div className="user-detail-main-content">
            <div className="user-detail-header">
              <div className="d-flex justify-content-between align-items-center">
                <h2 className="page-title">User Details</h2>
                <button
                  onClick={() => navigate(backNav.path)}
                  className="btn-back"
                >
                  {backNav.text}
                </button>
              </div>
            </div>
            <div className="alert alert-danger">
              <h4>Error Loading User</h4>
              <p>Unable to load user data.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-detail-container">
      <div className="container">
        <div className="user-detail-main-content">
          <div className="user-detail-header">
            <div className="d-flex justify-content-between align-items-center">
              <h2 className="page-title">User Details</h2>
              <button
                onClick={() => navigate(backNav.path)}
                className="btn-back"
              >
                {backNav.text}
              </button>
            </div>
          </div>

          <Row>
            <Col md={6}>
              <Card className="user-detail-card mb-4">
                <Card.Header>
                  <h4>Personal Information</h4>
                </Card.Header>
                <Card.Body>
                  <Row>
                    <Col sm={6}>
                      <div className="user-detail-info">
                        <strong>Name:</strong>
                        <p>{user.contactName}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="user-detail-info">
                        <strong>Email:</strong>
                        <p>{user.email}</p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col sm={6}>
                      <div className="user-detail-info">
                        <strong>Registered:</strong>
                        <p>{formatDate(user.createdAt)}</p>
                      </div>
                    </Col>
                    <Col sm={6}>
                      <div className="user-detail-info">
                        <strong>Status:</strong>
                        <p><span className={`badge bg-${user.isAdmin ? 'warning' : 'success'}`}>
                          {user.isAdmin ? 'Admin' : 'Active'}
                        </span></p>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>

            <Col md={6}>
              <Card className="user-detail-card mb-4">
                <Card.Header>
                  <h4>{user.isOrgAdmin ? 'Organisation Information' : 'School Information'}</h4>
                </Card.Header>
                <Card.Body>
                  {user.isOrgAdmin ? (
                    <>
                      <Row>
                        <Col sm={6}>
                          <div className="user-detail-info">
                            <strong>Subscription ID:</strong>
                            <p>{user.organizationId}</p>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="user-detail-info">
                            <strong>Subscription Code:</strong>
                            <p>{user.subScriptionCode}</p>
                          </div>
                        </Col>
                      </Row>
                    </>
                  ) : (
                    <>
                      <Row>
                        <Col sm={6}>
                          <div className="user-detail-info">
                            <strong>School:</strong>
                            <p>{user.schoolName}</p>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="user-detail-info">
                            <strong>Post Code:</strong>
                            <p>{user.postZipCode}</p>
                          </div>
                        </Col>
                      </Row>
                      <Row>
                        <Col sm={6}>
                          <div className="user-detail-info">
                            <strong>Teacher Code:</strong>
                            <p>{user.tchCode || user.teacherCode || 'Not assigned'}</p>
                          </div>
                        </Col>
                        <Col sm={6}>
                          <div className="user-detail-info">
                            <strong>School Code:</strong>
                            <p>{user.SchoolCode || user.schoolCode}</p>
                          </div>
                        </Col>
                      </Row>
                    </>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>

        </div>
      </div>
    </div>
  );
};

export default User; 
